import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { inr } from "@/lib/api";
import { Sparkles, PartyPopper } from "lucide-react";

const COLORS = ["#ff5c8a", "#ffe94d", "#60a5fa", "#4ade80", "#fb923c", "#c084fc", "#2dd4bf", "#f472b6"];

function buildSegments(result) {
  const lo = Number(result.value_min), hi = Number(result.value_max ?? result.value_min), n = 8;
  const step = result.type === "percent" ? 1 : 50;
  const vals = [];
  for (let i = 0; i < n; i++) vals.push(Math.round((lo + ((hi - lo) * i) / (n - 1)) / step) * step);
  const target = Number(result.value);
  let idx = vals.findIndex((v) => v === target);
  if (idx === -1) { idx = vals.reduce((best, v, i) => (Math.abs(v - target) < Math.abs(vals[best] - target) ? i : best), 0); vals[idx] = target; }
  // shuffle positions deterministically so winner isn't always in same slot
  const order = [3, 0, 5, 2, 7, 4, 1, 6];
  const segs = order.map((o) => vals[o]);
  return { segs, winner: segs.indexOf(target) };
}

export const SpinWheel = ({ result, onDone }) => {
  const [phase, setPhase] = useState("ready");
  const [rot, setRot] = useState(0);
  const { segs, winner } = useMemo(() => (result ? buildSegments(result) : { segs: [], winner: 0 }), [result]);
  const n = segs.length, seg = 360 / (n || 1);
  const fmt = (v) => (result?.type === "percent" ? `${v}%` : inr(v));
  useEffect(() => { if (result) { setPhase("ready"); setRot(0); } }, [result]);
  const spin = () => {
    setPhase("spinning");
    const target = 360 * 6 + (360 - (winner * seg + seg / 2));
    requestAnimationFrame(() => setRot(target));
    setTimeout(() => setPhase("won"), 4600);
  };
  if (!result) return null;
  const bg = `conic-gradient(${segs.map((_, i) => `${COLORS[i % COLORS.length]} ${i * seg}deg ${(i + 1) * seg}deg`).join(", ")})`;
  return (
    <Dialog open onOpenChange={(o) => !o && phase !== "spinning" && onDone()}>
      <DialogContent className="max-w-sm text-center overflow-hidden bg-[#0b1026] text-white border-white/10" data-testid="spin-wheel">
        <div className="pointer-events-none absolute inset-0">{[...Array(14)].map((_, i) => <span key={i} className="festive-spark" style={{ left: `${(i * 37) % 100}%`, top: `${(i * 53) % 90}%`, animationDelay: `${(i % 7) * 0.4}s` }} />)}</div>
        <div className="relative">
          <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.25em] text-[#ffe94d]"><Sparkles className="h-3.5 w-3.5" /> Lucky coupon</p>
          <h3 className="font-heading text-2xl mt-1">{phase === "won" ? "You won!" : `Spin to reveal your ${result.code} discount`}</h3>
          <div className="relative mx-auto mt-5 h-64 w-64">
            <div className="absolute left-1/2 -top-3 -translate-x-1/2 z-20 h-0 w-0 border-l-[12px] border-r-[12px] border-t-[22px] border-l-transparent border-r-transparent border-t-white drop-shadow" />
            <div className="h-full w-full rounded-full border-[6px] border-white shadow-[0_0_0_6px_rgba(255,255,255,0.15),0_20px_60px_rgba(0,0,0,0.5)]" style={{ background: bg, transform: `rotate(${rot}deg)`, transition: phase === "spinning" ? "transform 4.5s cubic-bezier(0.12, 0.8, 0.12, 1)" : "none" }} data-testid="wheel">
              {segs.map((v, i) => (
                <span key={i} className="absolute left-1/2 top-1/2 text-[13px] font-black text-slate-900 drop-shadow-sm" style={{ transform: `rotate(${i * seg + seg / 2}deg) translateY(-92px) translateX(-50%)`, transformOrigin: "0 0" }}>{fmt(v)}</span>
              ))}
            </div>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-14 w-14 rounded-full bg-white grid place-items-center shadow-lg z-10"><PartyPopper className="h-6 w-6 text-[#d62976]" /></div>
          </div>
          <AnimatePresence mode="wait">
            {phase === "won" ? (
              <motion.div key="won" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className="mt-5 space-y-3">
                <p className="font-heading text-4xl text-[#ffe94d]" data-testid="wheel-result">{fmt(result.value)} OFF</p>
                <p className="text-sm text-white/80">That's <b>{inr(result.discount)}</b> off your trip total.</p>
                <Button className="w-full rounded-full bg-[#ffe94d] text-slate-900 hover:bg-[#ffd60a] font-bold" onClick={onDone} data-testid="wheel-apply">Apply my discount</Button>
              </motion.div>
            ) : (
              <motion.div key="spin" exit={{ opacity: 0 }} className="mt-5">
                <Button className="w-full rounded-full bg-[#ff5c8a] hover:bg-[#e94c79] text-white font-bold text-base h-11" onClick={spin} disabled={phase === "spinning"} data-testid="wheel-spin">{phase === "spinning" ? "Spinning…" : "Spin the wheel"}</Button>
                <p className="text-[11px] text-white/60 mt-2">Discount between {fmt(result.value_min)} and {fmt(result.value_max)} — locked to your booking.</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};
